import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import TimeoutLink from 'apollo-link-timeout';

import { apiHost } from '@savvy/deprecated-shared/api/api-host';
import { LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';

const timeoutLink = new TimeoutLink(30000);

const httpLink = createHttpLink({
  uri: `${apiHost}/graphql`,
  credentials: 'include',
});

const GENERAL_AUTH_SCHEME = 'Bearer';

const authLink = setContext(async (_, { headers }) => {
  const accessToken = await LocalStorage.getItem('accessToken');

  let authHeader = null;
  if (accessToken) {
    authHeader = `${GENERAL_AUTH_SCHEME} ${accessToken}`;
  }

  return {
    headers: {
      ...(authHeader && { authorization: authHeader }),
      ...headers,
    },
  };
});

let onNetworkAuthError: (() => void) | null = null;

export const setOnNetworkAuthError = (handler: (() => void) | null) => {
  onNetworkAuthError = handler;
};

const CLIENT_OUTDATED_CODE = 'CLIENT_OUTDATED';

export type ClientOutdatedInfo = {
  readonly operationName: string;
};

let onClientOutdated: ((info: ClientOutdatedInfo) => void) | null = null;

/**
 * Register the handler invoked when Rails reports the client's query is no longer
 * valid against the live schema — `ClientOutdatedClassifier` re-tags schema-drift
 * validation errors with `extensions.code = 'CLIENT_OUTDATED'`. Sound as a
 * client-is-behind signal only because Rails always deploys before Vercel; see the
 * deploy-order invariant in `docs/plans/2026-07-23-version-skew-handling.md`.
 *
 * The version-skew refresh controller registers this; pass null to clear.
 */
export const setOnClientOutdated = (handler: ((info: ClientOutdatedInfo) => void) | null) => {
  onClientOutdated = handler;
};

let apolloClient: ApolloClient<unknown> | null;
export const createApolloClient = () => {
  if (apolloClient) return apolloClient;
  const errorLink = onError(({ networkError, graphQLErrors, operation }) => {
    if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
      onNetworkAuthError?.();
    }
    // Schema drift arrives as a GraphQL validation error at HTTP 200, so it never
    // reaches the networkError branch above.
    if (graphQLErrors?.some((error) => error.extensions?.['code'] === CLIENT_OUTDATED_CODE)) {
      onClientOutdated?.({ operationName: operation.operationName });
    }
  });
  apolloClient = new ApolloClient({
    connectToDevTools: true,
    link: ApolloLink.from([errorLink, timeoutLink.concat(authLink.concat(httpLink))]),
    cache: new InMemoryCache({
      typePolicies: {
        // A link read from the other end is the same row saying the opposite thing: `A blocked_by B`
        // and `B blocks A` are one row with id 9, and `task` is whichever end the reader is not on.
        // Normalizing by that id let the second task's copy overwrite the first's, so walking from A
        // to B and back showed A blocking itself. Held inline under each task instead.
        TaskLink: {
          keyFields: false,
        },
        // Same shape as TaskLink: one affiliation row, read from either household, whose `household`
        // is the other end. Normalized, the second household's copy would overwrite the first's.
        AffiliatedHousehold: {
          keyFields: false,
        },
        Query: {
          fields: {
            alerts: {
              // Every arg that narrows the feed must be a key arg, or a filter change is a cache hit with no refetch; `after` stays out so the merge fn appends pages within one entry.
              keyArgs: [
                'userId',
                'alertTypes',
                'primaryAdvisorIds',
                'householdIds',
                'status',
                'since',
                'first',
                'query',
              ],
              merge(
                existing: { edges?: readonly unknown[]; pageInfo?: unknown; totalCount?: number } | undefined,
                incoming: { edges?: readonly unknown[]; pageInfo?: unknown; totalCount?: number },
                { args }: { args?: { after?: string | null } | null },
              ) {
                if (args?.after !== undefined && args.after !== null) {
                  return {
                    ...incoming,
                    edges: [...(existing?.edges ?? []), ...(incoming.edges ?? [])],
                  };
                }
                return incoming;
              },
            },
          },
        },
      },
    }),
  });
  return apolloClient;
};
